import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, errMsg } from "@/lib/api";
import { AdminCard, PageTitle } from "@/pages/admin/AdminLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FIELDS = [
  { k: "company_name", l: "Nome azienda" },
  { k: "phone", l: "Telefono" },
  { k: "whatsapp", l: "Numero WhatsApp (con prefisso, es. +39 333 1234567)" },
  { k: "email", l: "Email pubblica" },
  { k: "address", l: "Indirizzo" },
  { k: "hours", l: "Orari" },
  { k: "instagram", l: "Link Instagram" },
  { k: "facebook", l: "Link Facebook" },
];

const REVIEW_FIELDS = [
  { k: "google_reviews_url", l: "Link recensioni Google", w: 2 },
  { k: "google_rating", l: "Voto medio Google (es. 4.9)", type: "number", step: "0.1" },
  { k: "google_count", l: "Numero recensioni Google", type: "number" },
  { k: "tripadvisor_url", l: "Link Tripadvisor", w: 2 },
  { k: "tripadvisor_rating", l: "Voto medio Tripadvisor", type: "number", step: "0.1" },
  { k: "tripadvisor_count", l: "Numero recensioni Tripadvisor", type: "number" },
];

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminApi.get("/admin/settings").then((r) => setS(r.data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));
      const r = await adminApi.put("/admin/settings", { ...s, google_rating: num(s.google_rating), google_count: Math.round(num(s.google_count)), tripadvisor_rating: num(s.tripadvisor_rating), tripadvisor_count: Math.round(num(s.tripadvisor_count)) });
      setS(r.data);
      toast.success("Impostazioni salvate: il sito è aggiornato");
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  if (!s) return <p className="text-ocean/50">Caricamento...</p>;
  const cls = "w-full h-11 px-4 rounded-xl border border-sand bg-white text-sm text-ocean focus:outline-none focus:border-sea";

  return (
    <div data-testid="admin-settings">
      <PageTitle title="Impostazioni" sub="Contatti, avvisi email, recensioni e condizioni commerciali. Solo il titolare può salvare." />
      <form onSubmit={save} className="space-y-6 max-w-3xl">
        <AdminCard className="p-6 md:p-8">
          <h2 className="font-serif text-xl text-ocean mb-1">Avvisi nuove prenotazioni</h2>
          <p className="text-sm text-ocean/55 mb-5">A questo indirizzo arriva un'email per ogni nuova prenotazione o richiesta.</p>
          <input type="email" className={cls} value={s.notify_email || ""} onChange={(e) => setS({ ...s, notify_email: e.target.value })} placeholder="tuaemail@esempio.it" data-testid="settings-notify_email" />
        </AdminCard>
        <AdminCard className="p-6 md:p-8">
          <h2 className="font-serif text-xl text-ocean mb-5">Contatti pubblici</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {FIELDS.map(({ k, l }) => (
              <div key={k} className={k === "address" || k === "whatsapp" ? "md:col-span-2" : ""}>
                <label className="block text-[11px] tracking-[0.18em] uppercase text-ocean/55 mb-2">{l}</label>
                <input className={cls} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`settings-${k}`} />
              </div>
            ))}
          </div>
        </AdminCard>
        <AdminCard className="p-6 md:p-8">
          <h2 className="font-serif text-xl text-ocean mb-1">Recensioni Google e Tripadvisor</h2>
          <p className="text-sm text-ocean/55 mb-5">Se inserisci i link, in home appaiono i pulsanti per leggere le recensioni. Voto e numero li aggiorni tu (lascia 0 per non mostrarli).</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {REVIEW_FIELDS.map(({ k, l, w, type, step }) => (
              <div key={k} className={w === 2 ? "md:col-span-2" : ""}>
                <label className="block text-[11px] tracking-[0.18em] uppercase text-ocean/55 mb-2">{l}</label>
                <input type={type || "text"} step={step} min={type ? 0 : undefined} max={k.endsWith("rating") ? 5 : undefined} className={cls} value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`settings-${k}`} />
              </div>
            ))}
          </div>
        </AdminCard>
        <AdminCard className="p-6 md:p-8">
          <h2 className="font-serif text-xl text-ocean mb-1">Politica di cancellazione</h2>
          <p className="text-sm text-ocean/55 mb-5">Viene mostrata sulle schede tour solo quando lo stato è «Approvata». Finché è in attesa, il sito non promette alcuna condizione.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-[11px] tracking-[0.18em] uppercase text-ocean/55 mb-2">Stato</label>
              <Select value={s.cancellation_policy_status || "pending"} onValueChange={(v) => setS({ ...s, cancellation_policy_status: v })}>
                <SelectTrigger className="h-11 rounded-xl border-sand bg-white" data-testid="settings-cancellation-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="pending">In attesa di approvazione (non mostrata)</SelectItem>
                  <SelectItem value="approved">Approvata (mostrata sul sito)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {[["it", "Italiano"], ["en", "English"], ["es", "Español"], ["fr", "Français"]].map(([l, n]) => (
              <div key={l} className={l === "it" ? "" : ""}>
                <label className="block text-[11px] tracking-[0.18em] uppercase text-ocean/55 mb-2">Testo {n}</label>
                <textarea rows={3} className="w-full px-4 py-3 rounded-xl border border-sand bg-white text-sm text-ocean focus:outline-none focus:border-sea resize-none" value={s.cancellation_policy?.[l] || ""} onChange={(e) => setS({ ...s, cancellation_policy: { ...(s.cancellation_policy || {}), [l]: e.target.value } })} data-testid={`settings-cancellation-${l}`} />
              </div>
            ))}
          </div>
        </AdminCard>
        <AdminCard className="p-6 md:p-8">
          <h2 className="font-serif text-xl text-ocean mb-1">Privacy e dati legali (GDPR)</h2>
          <p className="text-sm text-ocean/55 mb-5">Compaiono nella pagina pubblica «Informativa privacy» (/privacy) come titolare del trattamento. Finché sono vuoti, la pagina indica che i dati legali sono in fase di definizione.</p>
          <div className="grid grid-cols-1 gap-5">
            <div>
              <label className="block text-[11px] tracking-[0.18em] uppercase text-ocean/55 mb-2">Ragione sociale, P.IVA e sede legale</label>
              <textarea rows={3} maxLength={600} className="w-full px-4 py-3 rounded-xl border border-sand bg-white text-sm text-ocean focus:outline-none focus:border-sea resize-none" placeholder={"Es. AMS Travel S.r.l.\nP.IVA 00000000000\nVia ..., 80100 Napoli (NA)"} value={s.legal_entity || ""} onChange={(e) => setS({ ...s, legal_entity: e.target.value })} data-testid="settings-legal_entity" />
            </div>
            <div>
              <label className="block text-[11px] tracking-[0.18em] uppercase text-ocean/55 mb-2">Email per richieste privacy (se diversa da quella pubblica)</label>
              <input type="email" className={cls} value={s.privacy_email || ""} onChange={(e) => setS({ ...s, privacy_email: e.target.value })} placeholder="privacy@esempio.it" data-testid="settings-privacy_email" />
            </div>
          </div>
        </AdminCard>
        <AdminCard className="p-6 md:p-8">
          <h2 className="font-serif text-xl text-ocean mb-1">Pagamenti online</h2>
          <p className="text-sm text-ocean/55">Disattivati. Il sito raccoglie richieste di prenotazione senza incasso: i pagamenti online verranno attivati solo dopo l'approvazione di un provider, delle condizioni di cancellazione e delle impostazioni operative.</p>
        </AdminCard>
        <button type="submit" disabled={saving} className="px-8 py-3.5 bg-ocean text-ivory text-sm font-medium tracking-wider uppercase rounded-xl disabled:opacity-60 btn-lift" data-testid="settings-save">{saving ? "Salvataggio..." : "Salva impostazioni"}</button>
      </form>
    </div>
  );
}
